/**
 * Account — design/PROTOTYPE-SPEC.md §16, Business profile.
 *
 * Was read-only until 26 August 2026: a clinic that mistyped its name at signup,
 * or wanted a new password, had no route that did not involve asking us. That is
 * not a self-serve product.
 *
 * ## Logo
 *
 * Arrived 10 October 2026, stored in Postgres rather than an object store —
 * see `modules/logo.ts` and DECISIONS.md.
 *
 * **Notification settings** arrived on 10 October 2026, once there was a digest
 * for them to control: one choice per person, off / weekly / daily. The spec's
 * other switches (low stock, WhatsApp, Telegram, lead days) are still absent
 * for the original reason — nothing sends those, and a switch that controls
 * nothing is worse than no switch.
 */
import {
  PLAN_LABEL,
  bpsToPercent,
  can,
  percentToBps,
  type Role as CoreRole,
  SUPPORT_EMAIL,
} from '@inventory-tracker/core';
import { useRef, useState, type FormEvent } from 'react';
import { PlanPicker } from './PlanPicker.js';
import {
  changePassword,
  removeLogo,
  saveContactDetails,
  setDigestFrequency,
  updateAccount,
  uploadLogo,
  type ContactDetails,
  type DigestFrequency,
  type Me,
} from '../lib/api.js';
import { c, primaryButton, sans } from '../theme.js';
import { PasswordInput } from './PasswordInput.js';
import { cardStyle } from './format.js';
import { prepareLogo, useLogoUrl } from './logo.js';

interface Props {
  me: Me;
  /** Re-fetches /auth/me so the sidebar and header pick up a renamed clinic. */
  onChanged: () => void;
}

export function AccountPage({ me, onChanged }: Props) {
  const mayEdit = can(me.user.role as CoreRole, 'manageSettings');

  const trialDaysLeft = me.subscription.trialEndsAt
    ? Math.max(
        0,
        Math.ceil((new Date(me.subscription.trialEndsAt).getTime() - Date.now()) / 86_400_000),
      )
    : null;

  return (
    <>
      <div style={{ ...cardStyle, maxWidth: 620, margin: '0 auto' }}>
        <Row
          label="Plan"
          value={`${PLAN_LABEL[me.subscription.plan]} · ${me.subscription.cycle}`}
        />
        <Row label="Subscription" value={me.subscription.status} />
        {trialDaysLeft !== null && (
          <Row
            label="Trial"
            value={
              trialDaysLeft > 0
                ? `${trialDaysLeft} day${trialDaysLeft === 1 ? '' : 's'} remaining`
                : 'Ended'
            }
          />
        )}
        <Row label="Signed in as" value={`${me.user.name} · ${me.user.role}`} />
        <Row label="Email" value={me.user.email} />
        <Row label="Help" value={SUPPORT_EMAIL} href={`mailto:${SUPPORT_EMAIL}`} last />
      </div>

      <BusinessProfile me={me} mayEdit={mayEdit} onChanged={onChanged} />
      <Logo me={me} mayEdit={mayEdit} onChanged={onChanged} />
      <Contact me={me} mayEdit={mayEdit} onChanged={onChanged} />
      <ExpiryEmails me={me} onChanged={onChanged} />
      <ChangePassword />

      <PlanPicker me={me} mayManage={can(me.user.role as CoreRole, 'manageBilling')} />
    </>
  );
}

// --------------------------------------------------------------- profile

function BusinessProfile({
  me,
  mayEdit,
  onChanged,
}: {
  me: Me;
  mayEdit: boolean;
  onChanged: () => void;
}) {
  const [clinicName, setClinicName] = useState(me.account.clinicName);
  const [tagline, setTagline] = useState(me.account.tagline ?? '');
  // Held as a string so the field can be empty mid-edit. Turning "" into 0 on
  // every keystroke makes the input impossible to clear.
  const [vat, setVat] = useState(String(bpsToPercent(me.account.vatRateBps)));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const dirty =
    clinicName !== me.account.clinicName ||
    tagline !== (me.account.tagline ?? '') ||
    vat !== String(bpsToPercent(me.account.vatRateBps));

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSaved(false);
    // Never a dead button: say why nothing happened.
    if (!dirty) {
      setError('You have not changed anything here yet, so there is nothing to save.');
      return;
    }

    const percent = Number(vat);
    if (!Number.isFinite(percent) || percent < 0 || percent > 100) {
      setError('VAT rate must be between 0 and 100');
      return;
    }

    setBusy(true);
    try {
      await updateAccount({
        clinicName: clinicName.trim(),
        // An empty box means "no tagline", which is null rather than "".
        tagline: tagline.trim() === '' ? null : tagline.trim(),
        vatRateBps: percentToBps(percent),
      });
      setSaved(true);
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title="Business profile">
      {!mayEdit && (
        <p style={{ fontSize: 13, color: c.muted2, margin: '0 0 14px', lineHeight: 1.7 }}>
          Only an owner or manager can change these.
        </p>
      )}

      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Text
          id="clinicName"
          label="Business name"
          value={clinicName}
          onChange={setClinicName}
          disabled={!mayEdit || busy}
        />
        <Text
          id="tagline"
          label="Tagline"
          value={tagline}
          onChange={setTagline}
          disabled={!mayEdit || busy}
          placeholder="Appears under your name on exports"
        />
        <Text
          id="vat"
          label="VAT rate (%)"
          value={vat}
          onChange={setVat}
          disabled={!mayEdit || busy}
          inputMode="decimal"
          hint="Used on exports and reports. UK standard rate is 20%."
        />

        {error && <Notice tone="error">{error}</Notice>}
        {saved && !dirty && <Notice tone="ok">Saved.</Notice>}

        {mayEdit && (
          <button
            type="submit"
            disabled={busy}
            style={{
              ...primaryButton,
              width: 'auto',
              alignSelf: 'flex-start',
              height: 42,
              padding: '0 22px',
              opacity: busy ? 0.5 : 1,
            }}
          >
            {busy ? 'Saving…' : 'Save changes'}
          </button>
        )}
      </form>
    </Card>
  );
}

// ------------------------------------------------------------------ logo

/**
 * Upload, preview, remove. The image is resized in the browser first (see
 * logo.ts), so the only file a clinic owner can pick that fails is one that is
 * not an image at all.
 *
 * `onChanged` re-fetches /auth/me, which carries the new `logoVersion` — that
 * is what makes the sidebar swap to the new logo without a page reload.
 */
function Logo({ me, mayEdit, onChanged }: { me: Me; mayEdit: boolean; onChanged: () => void }) {
  const url = useLogoUrl(me.account.logoVersion);
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function choose(file: File | undefined) {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const prepared = await prepareLogo(file);
      await uploadLogo(prepared.contentType, prepared.dataBase64);
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not upload that image');
    } finally {
      setBusy(false);
      // Clear the input so choosing the same file again still fires `change`.
      if (input.current) input.current.value = '';
    }
  }

  async function remove() {
    setBusy(true);
    setError(null);
    try {
      await removeLogo();
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not remove the logo');
    } finally {
      setBusy(false);
    }
  }

  const outline: React.CSSProperties = {
    height: 38,
    padding: '0 16px',
    background: c.white,
    border: `1px solid ${c.borderStrong}`,
    borderRadius: 980,
    fontFamily: sans,
    fontSize: 13.5,
    fontWeight: 500,
    color: c.ink,
    cursor: busy ? 'default' : 'pointer',
    opacity: busy ? 0.5 : 1,
  };

  return (
    <Card title="Logo">
      <div style={{ display: 'flex', gap: 18, alignItems: 'center', flexWrap: 'wrap' }}>
        <div
          style={{
            // The same shape as the tile it appears on in the sidebar.
            width: 212,
            height: 72,
            padding: 10,
            boxSizing: 'border-box',
            borderRadius: 12,
            border: `1px solid ${c.borderStrong}`,
            background: c.white,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            flexShrink: 0,
          }}
        >
          {url ? (
            <img
              src={url}
              alt={`${me.account.clinicName} logo`}
              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
            />
          ) : (
            <span style={{ fontFamily: sans, letterSpacing: '-0.03em', fontSize: 30, fontWeight: 300, color: c.faint }}>
              {me.account.clinicName.trim().charAt(0).toUpperCase() || '—'}
            </span>
          )}
        </div>

        <div style={{ flex: 1, minWidth: 220 }}>
          <p style={{ fontSize: 13, color: c.muted, margin: '0 0 12px', lineHeight: 1.7 }}>
            Shown in the sidebar for everyone at {me.account.clinicName}. PNG, JPEG or WebP; we
            resize it for you.
          </p>
          {mayEdit ? (
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <input
                ref={input}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                aria-label="Choose a logo image"
                onChange={(e) => void choose(e.target.files?.[0])}
                style={{ display: 'none' }}
              />
              <button
                type="button"
                disabled={busy}
                onClick={() => input.current?.click()}
                style={outline}
              >
                {busy ? 'Working…' : me.account.logoVersion ? 'Replace logo' : 'Upload logo'}
              </button>
              {me.account.logoVersion && (
                <button type="button" disabled={busy} onClick={() => void remove()} style={outline}>
                  Remove
                </button>
              )}
            </div>
          ) : (
            <p style={{ fontSize: 12.5, color: c.faint, margin: 0 }}>
              An owner or manager can change it.
            </p>
          )}
        </div>
      </div>

      {error && (
        <div style={{ marginTop: 12 }}>
          <Notice tone="error">{error}</Notice>
        </div>
      )}
    </Card>
  );
}

// --------------------------------------------------------------- contact

const CONTACT_FORM: {
  key: keyof ContactDetails;
  label: string;
  placeholder?: string;
  autoComplete?: string;
}[] = [
  { key: 'phone', label: 'Phone', placeholder: '020 0000 0000', autoComplete: 'tel' },
  { key: 'website', label: 'Website', placeholder: 'yourclinic.co.uk', autoComplete: 'url' },
  { key: 'line1', label: 'Address line 1', autoComplete: 'address-line1' },
  { key: 'line2', label: 'Address line 2', autoComplete: 'address-line2' },
  { key: 'city', label: 'Town or city', autoComplete: 'address-level2' },
  { key: 'postcode', label: 'Postcode', autoComplete: 'postal-code' },
  { key: 'companyNumber', label: 'Company number' },
  { key: 'vatNumber', label: 'VAT number' },
];

/**
 * design/PROTOTYPE-SPEC.md §16, "contact/registration details". Details of the
 * BUSINESS. Saved as one form: what is on screen when Save is pressed is what
 * is stored, so emptying a box removes that detail.
 */
function Contact({ me, mayEdit, onChanged }: { me: Me; mayEdit: boolean; onChanged: () => void }) {
  const [form, setForm] = useState<ContactDetails>(me.account.contact);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const dirty = CONTACT_FORM.some(
    (f) => (form[f.key] ?? '').trim() !== (me.account.contact[f.key] ?? ''),
  );

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSaved(false);
    // Never a dead button: say why nothing happened.
    if (!dirty) {
      setError('You have not changed anything here yet, so there is nothing to save.');
      return;
    }
    setBusy(true);
    try {
      const res = await saveContactDetails(form);
      setForm(res.contact);
      setSaved(true);
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save those details');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title="Contact and registration">
      <p style={{ fontSize: 13, color: c.muted, margin: '0 0 16px', lineHeight: 1.7 }}>
        Your business’s own details. All optional.
      </p>
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: 14,
          }}
        >
          {CONTACT_FORM.map((f) => (
            <Text
              key={f.key}
              id={`contact-${f.key}`}
              label={f.label}
              value={form[f.key] ?? ''}
              onChange={(v) => {
                setForm((prev) => ({ ...prev, [f.key]: v }));
                setSaved(false);
              }}
              disabled={busy || !mayEdit}
              {...(f.placeholder ? { placeholder: f.placeholder } : {})}
              {...(f.autoComplete ? { autoComplete: f.autoComplete } : {})}
            />
          ))}
        </div>

        {error && <Notice tone="error">{error}</Notice>}
        {saved && <Notice tone="ok">Saved.</Notice>}

        {mayEdit ? (
          <button
            type="submit"
            disabled={busy}
            style={{
              ...primaryButton,
              width: 'auto',
              alignSelf: 'flex-start',
              height: 42,
              padding: '0 22px',
              opacity: busy ? 0.5 : 1,
            }}
          >
            {busy ? 'Saving…' : 'Save details'}
          </button>
        ) : (
          <p style={{ fontSize: 12.5, color: c.faint, margin: 0 }}>
            An owner or manager can change these.
          </p>
        )}
      </form>
    </Card>
  );
}

// ---------------------------------------------------------- expiry emails

const FREQUENCY_OPTIONS: { value: DigestFrequency; label: string; detail: string }[] = [
  {
    value: 'weekly',
    label: 'Weekly',
    detail:
      'Every Monday morning, if anything has expired, goes within four weeks, or needs reordering.',
  },
  {
    value: 'daily',
    label: 'Daily',
    detail:
      'Every morning while something needs attention. It repeats until the stock is dealt with.',
  },
  {
    value: 'off',
    label: 'Off',
    detail: 'No stock emails. The alerts on your dashboard still show the same stock.',
  },
];

/**
 * One person's choice about their own inbox — it changes nothing for anyone
 * else at the clinic, and the card says so, because "I turned the emails off"
 * must not be read as "the clinic is no longer warned".
 *
 * Saves on click rather than behind a button: it is one field, and a radio that
 * looks selected but is not saved until a second control is found is how
 * someone ends up still receiving email they believe they stopped.
 */
function ExpiryEmails({ me, onChanged }: { me: Me; onChanged: () => void }) {
  const [value, setValue] = useState<DigestFrequency>(me.user.digestFrequency);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // The digest goes to the people who can order stock. Showing staff three
  // options that do nothing would be the inert switch this page has avoided.
  if (me.user.role === 'staff') {
    return (
      <Card title="Stock emails">
        <p style={{ fontSize: 13, color: c.muted, margin: 0, lineHeight: 1.7 }}>
          Stock emails go to owners and managers. You will still see expiring and low stock on the
          dashboard and the Expiry page.
        </p>
      </Card>
    );
  }

  async function choose(next: DigestFrequency) {
    if (busy || next === value) return;
    const previous = value;
    setValue(next);
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      await setDigestFrequency(next);
      setSaved(true);
      onChanged();
    } catch (err) {
      // Put the selection back: a radio showing a choice the server refused is
      // the exact failure described above.
      setValue(previous);
      setError(err instanceof Error ? err.message : 'Could not save that');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title="Stock emails">
      <p style={{ fontSize: 13, color: c.muted, margin: '0 0 14px', lineHeight: 1.7 }}>
        How often we email <strong style={{ color: c.ink }}>{me.user.email}</strong> about stock
        that has expired, is about to, or is running low. This is your own setting. It does not
        change what anyone else at {me.account.clinicName} receives.
      </p>

      <div role="radiogroup" aria-label="Stock email frequency" style={{ display: 'grid', gap: 8 }}>
        {FREQUENCY_OPTIONS.map((o) => {
          const on = value === o.value;
          return (
            <label
              key={o.value}
              style={{
                display: 'flex',
                gap: 12,
                alignItems: 'flex-start',
                padding: '11px 13px',
                borderRadius: 14,
                border: `1px solid ${on ? c.ink : c.borderStrong}`,
                background: on ? c.white : 'transparent',
                cursor: busy ? 'default' : 'pointer',
              }}
            >
              <input
                type="radio"
                name="digestFrequency"
                value={o.value}
                checked={on}
                disabled={busy}
                onChange={() => void choose(o.value)}
                style={{ marginTop: 3, accentColor: c.ink }}
              />
              <span>
                <span style={{ display: 'block', fontSize: 14, fontWeight: 500, color: c.ink }}>
                  {o.label}
                </span>
                <span style={{ display: 'block', fontSize: 12.5, color: c.muted, lineHeight: 1.6 }}>
                  {o.detail}
                </span>
              </span>
            </label>
          );
        })}
      </div>

      <div style={{ marginTop: error || saved ? 12 : 0 }}>
        {error && <Notice tone="error">{error}</Notice>}
        {saved && !error && (
          <Notice tone="ok">
            {value === 'off' ? 'Saved. Stock emails are off for you.' : 'Saved.'}
          </Notice>
        )}
      </div>
    </Card>
  );
}

// -------------------------------------------------------------- password

function ChangePassword() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setDone(false);

    // The button is never dead: pressing it with a box empty says which.
    const missing = [
      !current && 'your current password',
      !next && 'a new password',
      !confirm && 'the new password again',
    ].filter(Boolean);
    if (missing.length > 0) {
      setError(`Still needed: ${missing.join(', ')}.`);
      return;
    }

    // Checked here rather than server-side: it is a typo check, not a security
    // control, and the API never sees the confirmation.
    if (next !== confirm) {
      setError('Those passwords do not match');
      return;
    }

    setBusy(true);
    try {
      await changePassword(current, next);
      setDone(true);
      setCurrent('');
      setNext('');
      setConfirm('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not change your password');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title="Password">
      <p style={{ fontSize: 13, color: c.muted, margin: '0 0 16px', lineHeight: 1.7 }}>
        Changing your password signs you out everywhere else. This device stays signed in.
      </p>

      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Text
          id="currentPassword"
          label="Current password"
          type="password"
          value={current}
          onChange={setCurrent}
          disabled={busy}
          autoComplete="current-password"
        />
        <Text
          id="newPassword"
          label="New password"
          type="password"
          value={next}
          onChange={setNext}
          disabled={busy}
          autoComplete="new-password"
          hint="At least 10 characters. Length beats symbols."
        />
        <Text
          id="confirmPassword"
          label="Confirm new password"
          type="password"
          value={confirm}
          onChange={setConfirm}
          disabled={busy}
          autoComplete="new-password"
        />

        {error && <Notice tone="error">{error}</Notice>}
        {done && <Notice tone="ok">Password changed. Other devices have been signed out.</Notice>}

        <button
          type="submit"
          disabled={busy}
          style={{
            ...primaryButton,
            width: 'auto',
            alignSelf: 'flex-start',
            height: 42,
            padding: '0 22px',
            opacity: busy ? 0.5 : 1,
          }}
        >
          {busy ? 'Changing…' : 'Change password'}
        </button>
      </form>
    </Card>
  );
}

// ----------------------------------------------------------------- bits

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ ...cardStyle, maxWidth: 620, margin: '16px auto 0' }}>
      <h2 style={{ fontFamily: sans, letterSpacing: '-0.03em', fontWeight: 300, fontSize: 21, margin: '0 0 14px' }}>
        {title}
      </h2>
      {children}
    </div>
  );
}

function Text({
  id,
  label,
  value,
  onChange,
  disabled,
  type = 'text',
  placeholder,
  hint,
  autoComplete,
  inputMode,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  type?: string;
  placeholder?: string;
  hint?: string;
  autoComplete?: string;
  inputMode?: 'decimal';
}) {
  const Box = type === 'password' ? PasswordInput : 'input';
  return (
    <div>
      <label
        htmlFor={id}
        style={{
          display: 'block',
          fontSize: 14,
          color: c.label,
          fontWeight: 500,
          marginBottom: 6,
        }}
      >
        {label}
      </label>
      <Box
        id={id}
        {...(type === 'password' ? {} : { type })}
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        autoComplete={autoComplete}
        inputMode={inputMode}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: '100%',
          height: 44,
          padding: '0 13px',
          borderRadius: 12,
          border: `1px solid ${c.borderStrong}`,
          background: disabled ? c.well : c.white,
          color: disabled ? c.muted2 : c.ink,
          fontSize: 14,
          fontFamily: sans,
          boxSizing: 'border-box',
        }}
      />
      {hint && <p style={{ fontSize: 11.5, color: c.faint, margin: '6px 0 0' }}>{hint}</p>}
    </div>
  );
}

function Notice({ tone, children }: { tone: 'ok' | 'error'; children: React.ReactNode }) {
  const ok = tone === 'ok';
  return (
    <div
      role={ok ? undefined : 'alert'}
      style={{
        background: ok ? c.okBg : c.dangerBg,
        border: `1px solid ${ok ? '#BFD2C4' : c.dangerBorder}`,
        color: ok ? c.success : c.danger,
        borderRadius: 12,
        padding: '10px 13px',
        fontSize: 13,
        lineHeight: 1.6,
      }}
    >
      {children}
    </div>
  );
}

function Row({
  label,
  value,
  href,
  last,
}: {
  label: string;
  value: string;
  href?: string;
  last?: boolean;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
        padding: '13px 0',
        borderBottom: last ? 'none' : `1px solid ${c.border}`,
      }}
    >
      <span style={{ fontSize: 13.5, color: c.muted2 }}>{label}</span>
      {href ? (
        <a href={href} style={{ fontSize: 14, fontWeight: 500, color: c.green }}>
          {value}
        </a>
      ) : (
        <span style={{ fontSize: 14, fontWeight: 500 }}>{value}</span>
      )}
    </div>
  );
}
